import React, { useState, useEffect } from "react";
import { FaTimes, FaChevronLeft, FaChevronRight } from "react-icons/fa";
import FrameOneModal from "@/components/modals/modalComponents/FrameOneModal";

interface ScreenshotLightboxProps {
    isOpen: boolean;
    screenshots: string[];
    initialIndex: number;
    onClose: () => void;
}

const ScreenshotLightbox: React.FC<ScreenshotLightboxProps> = ({
    isOpen,
    screenshots,
    initialIndex,
    onClose,
}) => {
    const [currentIndex, setCurrentIndex] = useState(initialIndex);

    useEffect(() => {
        setCurrentIndex(initialIndex);
    }, [initialIndex]);

    const handlePrevious = () => {
        setCurrentIndex((prev) =>
            prev === 0 ? screenshots.length - 1 : prev - 1);
    };

    const handleNext = () => {
        setCurrentIndex((prev) =>
            prev === screenshots.length - 1 ? 0 : prev + 1);
    };

    const handleKeyDown = (e: KeyboardEvent) => {
        if (!isOpen) return;

        if (e.key === "ArrowLeft") {
            handlePrevious();
        } else if (e.key === "ArrowRight") {
            handleNext();
        } else if (e.key === "Escape") {
            onClose();
        }
    };

    useEffect(() => {
        document.addEventListener("keydown", handleKeyDown);
        return () => document.removeEventListener("keydown", handleKeyDown);
    }, [isOpen]);

    useEffect(() => {
        if (isOpen) {
            document.body.style.overflow = "hidden";
        } else {
            document.body.style.overflow = "unset";
        }

        return () => {
            document.body.style.overflow = "unset";
        };
    }, [isOpen]);

    if (!isOpen || screenshots.length === 0) return null;

    return (
        <FrameOneModal
            isOpen={isOpen}
            toggle={onClose}
            size="xl"
            contentClassName="screenshot-lightbox"
            disableOverlayDismiss={false}
        >
            <div className="screenshot-lightbox__container">
                {/* Close button */}
                <button
                    className="screenshot-lightbox__close"
                    onClick={onClose}
                    aria-label="Close lightbox"
                >
                    ✕
                </button>

                {/* Image container */}
                <div className="screenshot-lightbox__image-container">
                    <img
                        src={screenshots[currentIndex]}
                        alt={`Screenshot ${currentIndex + 1}`}
                        className="screenshot-lightbox__image"
                    />
                </div>

                {/* Navigation buttons */}
                {screenshots.length > 1 &&
                    <>
                        <button
                            className="screenshot-lightbox__nav screenshot-lightbox__nav--prev"
                            onClick={handlePrevious}
                            aria-label="Previous screenshot"
                        >
                            <FaChevronLeft />
                        </button>
                        <button
                            className="screenshot-lightbox__nav screenshot-lightbox__nav--next"
                            onClick={handleNext}
                            aria-label="Next screenshot"
                        >
                            <FaChevronRight />
                        </button>
                    </>
                }

                {/* Image counter */}
                <div className="screenshot-lightbox__counter">
                    {currentIndex + 1} of {screenshots.length}
                </div>

                {/* Thumbnail navigation */}
                {screenshots.length > 1 &&
                    <div className="screenshot-lightbox__thumbnails">
                        {screenshots.map((screenshot, index) =>
                            <button
                                key={index}
                                className={`screenshot-lightbox__thumbnail ${
                                    index === currentIndex ? "screenshot-lightbox__thumbnail--active" : ""
                                }`}
                                onClick={() => setCurrentIndex(index)}
                                aria-label={`Go to screenshot ${index + 1}`}
                            >
                                <img
                                    src={screenshot}
                                    alt={`Screenshot ${index + 1} thumbnail`}
                                />
                            </button>)}
                    </div>
                }
            </div>
        </FrameOneModal>
    );
};

export default ScreenshotLightbox;